// Public training quizzes served at /training/:quizSlug (no auth, reachable by
// link or QR code). Per the feature decisions: the questions live here in the
// frontend (hardcoded content) and scoring is done client-side. That means the
// correct answers ship in the JS bundle, acceptable for training quizzes, but
// not appropriate for high-stakes exams. If these ever become gated
// assessments, move grading behind the backend.
//
// CONTENT STATUS: five real quizzes, two EN/ES pairs transcribed verbatim from
// their Zoho forms plus the English-only QEW refresher (from a PDF):
//   - Koch Foods 10-question cert ('koch-foods-electrical-safety' + '-spanish'):
//     answer key customer-confirmed.
//   - Comprehensive 20-question cert ('electrical-safety' + '-spanish'):
//     answer key confirmed from the customer's "2024 Bowtie QEW Test Key" PDF.
//   - QEW refresher 10-question cert ('electrical-safety-refresher'), English
//     only: answer key supplied by the customer with the exam (2026-10-05).

// Default pass mark: 70% correct. Each quiz may override via `passingScore`.
export const PASSING_SCORE = 0.7

// Default class length printed on certificates ("Eight (8) hours of ..."). A
// quiz may override via `certificateHours`. Either way the Generate
// Certificates dialog opens on it and the admin can still change it.
export const DEFAULT_CERTIFICATE_HOURS = 8

// The NFPA 70E edition named by the default course line ("Electrical Safety &
// NFPA 70E <year> Standard Training") and by the Refresher's. The Generate
// Certificates dialog opens on it, and the API prints it on automatic
// certificates (training-quiz-keys). 2027 from 2026-11-04 (Angel); an exam may
// keep another edition with `nfpaYear` (Koch Foods stays on 2024).
export const DEFAULT_NFPA_YEAR = 2027

// The NFPA 70E edition an exam's certificates name: its own `nfpaYear`, or the
// default.
export function nfpaYearOf(quiz) {
  return (quiz && quiz.nfpaYear) || DEFAULT_NFPA_YEAR
}

export const QUIZZES = [
  {
    slug: 'koch-foods-electrical-safety',
    lang: 'en',
    // Short label for admin UI (tabs). The full title is too long there.
    shortTitle: 'Koch Foods (EN)',
    // Certificate family: EN + ES editions share a family so certs for both
    // can be generated together.
    family: 'koch-foods',
    // See the qualified-worker entries: the customer-facing family name, kept
    // separate from shortTitle so the tab label stays short.
    familyTitle: 'Koch Foods (NFPA 70E)',
    // A 4-hour course, not the default 8 (Angel, 2026-10-23). Both editions
    // carry it: the certificate is the family's, whichever language was taken.
    certificateHours: 4,
    // Koch Foods stays on the NFPA 70E 2024 edition when the others moved to
    // 2027 (Angel, 2026-11-04). Both editions, for the same reason as the hours.
    nfpaYear: 2024,
    title: 'Koch Foods: Electrical Safety for Qualified Electrical Worker',
    description:
      'Confirm your understanding of the electrical-safety practices required of a qualified electrical worker at Koch Foods.',
    // Shown as a banner above the questions (mirrors the source form).
    note: 'You may use your book during the test.',
    passingScore: PASSING_SCORE,
    // Questions/choices transcribed verbatim from the Koch Foods Zoho form.
    // Answer key confirmed by the customer (2026-07-17) as radio positions
    // 1,2,1,3,3,3,2,1,3,3 for q1..q10.
    questions: [
      {
        id: 'q1',
        prompt:
          'What is the name of the Standard for Electrical Safety in the workplace?',
        choices: [
          { id: 'a', text: 'NFPA 70E' },
          { id: 'b', text: 'NFPA 101' },
          { id: 'c', text: 'OSHA 30' },
          { id: 'd', text: 'ASTM F-18' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q2',
        prompt: 'What are the 3 hazards of electricity?',
        choices: [
          { id: 'a', text: 'Burns, cuts, and bruises' },
          { id: 'b', text: 'Shock, arc flash, and arc blast' },
          { id: 'c', text: 'Slips, trips, and falls' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q3',
        prompt:
          'A Safety Backup is REQUIRED anytime energized work is performed at Koch Foods over 50 volts AC/DC.',
        choices: [
          { id: 'a', text: 'True' },
          { id: 'b', text: 'False' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q4',
        prompt:
          'Energized work is not allowed unless it is _____________________.',
        choices: [
          { id: 'a', text: 'Demanded by your supervisor' },
          { id: 'b', text: 'Performed by the CEO and/or management team' },
          {
            id: 'c',
            text: 'JUSTIFIED for reasons of Additional Hazards, Infeasibility or Less than 50 volts',
          },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q5',
        prompt: 'What is the purpose for Lockout/Tagout (LOTO)?',
        choices: [
          { id: 'a', text: 'To help the lock manufacturers stay in business' },
          { id: 'b', text: 'To slow us down' },
          {
            id: 'c',
            text: 'To keep someone from coming behind us and turning on what we have turned off to work safely',
          },
          { id: 'd', text: 'All of the above' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q6',
        prompt:
          'At Koch Foods you are authorized to work on equipment you are familiar with as long as the voltage does not exceed ________ volts.',
        choices: [
          { id: 'a', text: '50' },
          { id: 'b', text: '110' },
          { id: 'c', text: '480' },
          { id: 'd', text: '4,160' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q7',
        prompt:
          'There are how many steps to confirm a circuit is de-energized?',
        choices: [
          { id: 'a', text: '3' },
          { id: 'b', text: '8' },
          { id: 'c', text: '6' },
          { id: 'd', text: '4' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q8',
        prompt:
          'At Koch Foods, what is the highest level of incident energy you are authorized to work on?',
        choices: [
          { id: 'a', text: '12 cal/cm^2' },
          { id: 'b', text: '25 cal/cm^2' },
          { id: 'c', text: '40 cal/cm^2' },
          { id: 'd', text: '140 cal/cm^2' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q9',
        prompt:
          'Voltage rated rubber gloves must be tested _________________ and inspected ____________________.',
        choices: [
          { id: 'a', text: 'Annually, monthly' },
          { id: 'b', text: 'Annually, only if failure is suspected' },
          { id: 'c', text: 'Every six months, before each use' },
          { id: 'd', text: 'Annually, weekly' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q10',
        prompt: 'The number one rule of electrical safety is:',
        choices: [
          {
            id: 'a',
            text: 'Wear your PPE no matter what situation you find yourself in',
          },
          {
            id: 'b',
            text: 'Any dangerous job should be passed on to the oldest qualified person',
          },
          {
            id: 'c',
            text: 'Consider all circuits energized until you can confirm for yourself that it is actually de-energized',
          },
          { id: 'd', text: 'Blame your co-workers' },
        ],
        correctChoiceId: 'c',
      },
    ],
  },
  {
    // Spanish edition of the Koch Foods electrical-safety cert. Same questions,
    // order, and confirmed answer key (1,2,1,3,3,3,2,1,3,3) as the English
    // edition, transcribed from its Zoho form. `lang: 'es'` localizes the chrome.
    slug: 'koch-foods-electrical-safety-spanish',
    lang: 'es',
    shortTitle: 'Koch Foods (ES)',
    family: 'koch-foods',
    // See the qualified-worker entries: the customer-facing family name, kept
    // separate from shortTitle so the tab label stays short.
    familyTitle: 'Koch Foods (NFPA 70E)',
    // A 4-hour course, not the default 8 (Angel, 2026-10-23). Both editions
    // carry it: the certificate is the family's, whichever language was taken.
    certificateHours: 4,
    // Koch Foods stays on the NFPA 70E 2024 edition when the others moved to
    // 2027 (Angel, 2026-11-04). Both editions, for the same reason as the hours.
    nfpaYear: 2024,
    title:
      'Koch Foods: Seguridad eléctrica para trabajadores eléctricos calificados',
    description:
      'Confirme su conocimiento de las prácticas de seguridad eléctrica requeridas para un trabajador eléctrico calificado en Koch Foods.',
    note: 'Podrá usar su libro durante el examen.',
    passingScore: PASSING_SCORE,
    questions: [
      {
        id: 'q1',
        prompt:
          '¿Qué norma se usa para definir la seguridad eléctrica en el lugar de trabajo?',
        choices: [
          { id: 'a', text: 'NFPA 70E' },
          { id: 'b', text: 'NFPA 101' },
          { id: 'c', text: 'OSHA 30' },
          { id: 'd', text: 'ASTM F-18' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q2',
        prompt: 'Los tres peligros de la electricidad son:',
        choices: [
          { id: 'a', text: 'Quemaduras, cortes y contusiones' },
          {
            id: 'b',
            text: 'Descargas eléctricas, arcos eléctricos y explosiones de arco',
          },
          { id: 'c', text: 'Resbalones, tropiezos y caídas' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q3',
        prompt:
          'Se REQUIERE un respaldo de seguridad siempre que se realicen trabajos con energía eléctrica en Koch Foods por encima de 50 voltios CA/CC.',
        choices: [
          { id: 'a', text: 'Verdadero' },
          { id: 'b', text: 'Falso' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q4',
        prompt:
          'No se permite trabajar con energía a menos que sea _____________________.',
        choices: [
          { id: 'a', text: 'Exigido por su supervisor' },
          {
            id: 'b',
            text: 'Realizado por el director general y/o el equipo directivo',
          },
          {
            id: 'c',
            text: 'JUSTIFICADO por razones de riesgos adicionales, inviabilidad o menos de 50 voltios',
          },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q5',
        prompt: '¿Cuál es el propósito del bloqueo y etiquetado (LOTO)?',
        choices: [
          {
            id: 'a',
            text: 'Ayudar a los fabricantes de cerraduras a mantenerse en el negocio',
          },
          { id: 'b', text: 'Para frenarnos' },
          {
            id: 'c',
            text: 'Evitar que alguien venga detrás de nosotros y encienda lo que hemos apagado para trabajar de forma segura',
          },
          { id: 'd', text: 'Todas las anteriores' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q6',
        prompt:
          'En Koch Foods, usted está autorizado a trabajar con equipos con los que esté familiarizado, siempre y cuando el voltaje no supere los ________ voltios.',
        choices: [
          { id: 'a', text: '50' },
          { id: 'b', text: '110' },
          { id: 'c', text: '480' },
          { id: 'd', text: '4,160' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q7',
        prompt:
          '¿Cuántos pasos hay que seguir para confirmar que un circuito está desenergizado?',
        choices: [
          { id: 'a', text: '3' },
          { id: 'b', text: '8' },
          { id: 'c', text: '6' },
          { id: 'd', text: '4' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q8',
        prompt:
          'En Koch Foods, ¿cuál es el nivel más alto de energía de incidente con el que estás autorizado a trabajar?',
        choices: [
          { id: 'a', text: '12 cal/cm^2' },
          { id: 'b', text: '25 cal/cm^2' },
          { id: 'c', text: '40 cal/cm^2' },
          { id: 'd', text: '140 cal/cm^2' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q9',
        prompt:
          'Los guantes de hule con clasificación de voltaje deben someterse a pruebas ___________ e inspeccionarse __________.',
        choices: [
          { id: 'a', text: 'Anualmente, mensualmente.' },
          {
            id: 'b',
            text: 'Anualmente, solo si se sospecha que pueden fallar.',
          },
          { id: 'c', text: 'Cada seis meses, antes de cada uso.' },
          { id: 'd', text: 'Anualmente, semanalmente.' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q10',
        prompt: 'La regla número uno de la seguridad eléctrica es:',
        choices: [
          {
            id: 'a',
            text: 'Utilizar el equipo de protección personal (EPP) independientemente de la situación en la que se encuentre.',
          },
          {
            id: 'b',
            text: 'Cualquier trabajo peligroso debe delegarse a la persona cualificada de mayor edad.',
          },
          {
            id: 'c',
            text: 'Considerar que todos los circuitos están energizados hasta que pueda confirmar por sí mismo que realmente están desenergizados.',
          },
          { id: 'd', text: 'Culpar a sus compañeros de trabajo.' },
        ],
        correctChoiceId: 'c',
      },
    ],
  },
  {
    // Comprehensive "Electrical Safety for Qualified Electrical Worker" cert
    // (20 questions), transcribed verbatim from its Zoho form ("Test1").
    // Answer key confirmed from the customer's "2024 Bowtie QEW Test Key" PDF
    // (2026-07-17). q19 accepts two answers (see acceptedChoiceIds). Same key
    // applies to the Spanish edition below.
    slug: 'electrical-safety',
    lang: 'en',
    shortTitle: 'Qualified Worker (EN)',
    family: 'qualified-worker',
    // How the FAMILY is named on certificates, rosters and the certificate
    // email. Separate from shortTitle, which labels the tab and has to stay
    // short; this is the name a customer reads. Both editions carry it so
    // familyLabel resolves whichever tab is active.
    familyTitle: 'Qualified Workers (NFPA 70E)',
    title: 'Electrical Safety for Qualified Electrical Worker',
    description:
      'A comprehensive assessment of electrical-safety knowledge for a qualified electrical worker.',
    note: 'You may use your book during the test.',
    passingScore: PASSING_SCORE,
    questions: [
      {
        id: 'q1',
        prompt:
          'The restricted approach boundary for fixed equipment at 480 volts AC is:',
        choices: [
          { id: 'a', text: '4 feet' },
          { id: 'b', text: '1 inch' },
          { id: 'c', text: '1 foot' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q2',
        prompt:
          'We know that NFPA is nationally recognized for NFPA 70 (NEC) and NFPA 70B for Electrical Maintenance, which standard is Electrical Safety in the Workplace?',
        choices: [
          { id: 'a', text: 'NFPA 70E' },
          { id: 'b', text: 'NFPA 101' },
          { id: 'c', text: 'OSHA 30' },
          { id: 'd', text: 'ASTM F-18' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q3',
        prompt:
          'Only qualified persons may cross the limited approach boundary.',
        choices: [
          { id: 'a', text: 'True' },
          { id: 'b', text: 'False' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q4',
        prompt: 'The 3 hazards of electricity are:',
        choices: [
          { id: 'a', text: 'Burns, cuts, and bruises' },
          { id: 'b', text: 'Shock, arc flash, and arc blast' },
          { id: 'c', text: 'Slips, trips, and falls' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q5',
        prompt:
          'Onset of Second degree burns can occur at this calorie rating.',
        choices: [
          { id: 'a', text: '1 cal/cm^2' },
          { id: 'b', text: '2 cal/cm^2' },
          { id: 'c', text: '1.2 cal/cm^2' },
          { id: 'd', text: '4 cal/cm^2' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q6',
        prompt:
          'One may utilize the NFPA 70E 130.7 tables to calculate arc blast factors.',
        choices: [
          { id: 'a', text: 'True' },
          { id: 'b', text: 'False' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q7',
        prompt:
          'Energized work is not allowed unless it is _____________________.',
        choices: [
          { id: 'a', text: 'Demanded by your supervisor' },
          { id: 'b', text: 'Performed by the CEO and/or management team' },
          {
            id: 'c',
            text: 'JUSTIFIED for reasons of Additional Hazards, Infeasibility or Less than 50 volts',
          },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q8',
        prompt:
          'A Pocket Voltage Detector can be used to adequately verify the absence of voltage.',
        choices: [
          { id: 'a', text: 'True' },
          { id: 'b', text: 'False' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q9',
        prompt:
          'A Qualified Person is one who has _______________________ related to the construction and operation of electrical equipment and installations, and has received safety training to identify the hazards and reduce the associated risk.',
        choices: [
          { id: 'a', text: 'Experience finding the safety data sheet' },
          { id: 'b', text: 'Studied Safe Work Practices' },
          { id: 'c', text: 'Demonstrated skills and knowledge' },
          { id: 'd', text: 'Attended NFPA 70E training' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q10',
        prompt:
          'The amount of thermal energy, impressed on a surface, a certain distance from the source, generated during an electrical arc event is:',
        choices: [
          { id: 'a', text: 'Arc Flash' },
          { id: 'b', text: 'Incident Energy' },
          { id: 'c', text: 'Radiated Temperature' },
          { id: 'd', text: 'Calories' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q11',
        prompt:
          'OSHA requires the use of electrically rated rubber gloves (selected by voltage exposure) along with leather over-protectors when:',
        choices: [
          {
            id: 'a',
            text: 'Confirming absence of voltage on circuits above 50 volts',
          },
          { id: 'b', text: 'Troubleshooting circuits above 50 volts' },
          { id: 'c', text: 'Crossing the Restricted Approach Boundary' },
          { id: 'd', text: 'All of the above' },
        ],
        correctChoiceId: 'd',
      },
      {
        id: 'q12',
        prompt:
          'Voltage rated rubber gloves must be tested _________________ and inspected ____________________.',
        choices: [
          { id: 'a', text: 'Annually, monthly' },
          { id: 'b', text: 'Annually, only if failure is suspected' },
          { id: 'c', text: 'Every six months, before each use' },
          { id: 'd', text: 'Annually, weekly' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q13',
        prompt:
          'Exposure to voltage _______________ is considered a shock and electrocution hazard.',
        choices: [
          { id: 'a', text: '50 volts and above' },
          { id: 'b', text: '30 volts and above' },
          { id: 'c', text: '24 volts and above' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q14',
        prompt:
          'The arc flash boundary is determined to be the distance from the arc flash where incident energy drops to ___________.',
        choices: [
          { id: 'a', text: '200 degrees F' },
          { id: 'b', text: '1.0 cal/cm^2' },
          { id: 'c', text: '1.2 cal/cm^2' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q15',
        prompt: 'Barricades are set at (required by OSHA):',
        choices: [
          { id: 'a', text: 'The Restricted Approach Boundary only' },
          { id: 'b', text: 'The Limited Approach Boundary only' },
          { id: 'c', text: 'The Arc Flash Boundary only' },
          {
            id: 'd',
            text: 'The Limited Approach Boundary or the Arc Flash Boundary, whichever is greater',
          },
        ],
        correctChoiceId: 'd',
      },
      {
        id: 'q16',
        prompt:
          'Performing a "personal" Risk Assessment prior to engaging with an electrical circuit over 50 volts is:',
        choices: [
          { id: 'a', text: 'Optional' },
          {
            id: 'b',
            text: 'Understanding the probability and potential severity of injury associated with performing energized work.',
          },
          {
            id: 'c',
            text: 'Required by the IEEE 1584-2018 formula for calculating incident energy',
          },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q17',
        prompt:
          'What is the least effective means for reducing the risk associated with a hazard?',
        choices: [
          { id: 'a', text: 'Engineering controls' },
          { id: 'b', text: 'Administrative controls' },
          { id: 'c', text: 'Substitution' },
          { id: 'd', text: 'PPE' },
        ],
        correctChoiceId: 'd',
      },
      {
        id: 'q18',
        prompt: 'What is the purpose for Lockout/Tagout (LOTO)?',
        choices: [
          { id: 'a', text: 'To help the lock manufacturers stay in business' },
          { id: 'b', text: 'To slow us down' },
          {
            id: 'c',
            text: 'To keep someone from coming behind us and turning on what we have turned off to work safely',
          },
          { id: 'd', text: 'All of the above' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q19',
        prompt:
          'Combining 8 cal/cm^2 daily-wear with a 40 cal/cm^2 Arc Flash Suit gives a total system of 48 cal/cm^2.',
        choices: [
          { id: 'a', text: 'True' },
          { id: 'b', text: 'False' },
          { id: 'c', text: 'It depends on other factors' },
        ],
        // Answer key marks False (b); "It depends on other factors" (c) is
        // noted as also acceptable, so both count as correct.
        correctChoiceId: 'b',
        acceptedChoiceIds: ['b', 'c'],
      },
      {
        id: 'q20',
        prompt: 'The number one rule of electrical safety is:',
        choices: [
          {
            id: 'a',
            text: 'Wear your PPE no matter what situation you find yourself in',
          },
          {
            id: 'b',
            text: 'Any dangerous job should be passed on to the oldest qualified person',
          },
          {
            id: 'c',
            text: 'Consider all circuits energized until you can confirm for yourself that it is actually de-energized',
          },
          { id: 'd', text: 'Blame your co-workers' },
        ],
        correctChoiceId: 'c',
      },
    ],
  },
  {
    // Spanish edition of the comprehensive 20-question cert. Same questions,
    // order, and (customer-confirmed) answer key as 'electrical-safety'.
    // `lang: 'es'` localizes the chrome.
    slug: 'electrical-safety-spanish',
    lang: 'es',
    shortTitle: 'Qualified Worker (ES)',
    family: 'qualified-worker',
    // How the FAMILY is named on certificates, rosters and the certificate
    // email. Separate from shortTitle, which labels the tab and has to stay
    // short; this is the name a customer reads. Both editions carry it so
    // familyLabel resolves whichever tab is active.
    familyTitle: 'Qualified Workers (NFPA 70E)',
    title: 'Seguridad eléctrica para trabajadores eléctricos calificados',
    description:
      'Una evaluación integral de los conocimientos de seguridad eléctrica para un trabajador eléctrico calificado.',
    note: 'Podrá usar su libro durante el examen.',
    passingScore: PASSING_SCORE,
    questions: [
      {
        id: 'q1',
        prompt:
          'El límite de aproximación restringida para equipos fijos a 480 voltios de corriente alterna es:',
        choices: [
          { id: 'a', text: '4 pies' },
          { id: 'b', text: '1 pulgada' },
          { id: 'c', text: '1 pie' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q2',
        prompt:
          'Sabemos que la NFPA es reconocida a nivel nacional por la NFPA 70 (NEC) y la NFPA 70B para el mantenimiento eléctrico, ¿qué norma se usa para definir la seguridad eléctrica en el lugar de trabajo?',
        choices: [
          { id: 'a', text: 'NFPA 70E' },
          { id: 'b', text: 'NFPA 101' },
          { id: 'c', text: 'OSHA 30' },
          { id: 'd', text: 'ASTM F-18' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q3',
        prompt:
          'Solo las personas calificadas pueden cruzar el límite de aproximación restringida.',
        choices: [
          { id: 'a', text: 'Verdadero' },
          { id: 'b', text: 'Falso' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q4',
        prompt: 'Los tres peligros de la electricidad son:',
        choices: [
          { id: 'a', text: 'Quemaduras, cortes y contusiones' },
          {
            id: 'b',
            text: 'Descargas eléctricas, arcos eléctricos y explosiones de arco',
          },
          { id: 'c', text: 'Resbalones, tropiezos y caídas' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q5',
        prompt:
          'Las quemaduras de segundo grado pueden producirse con esta clasificación calórica:',
        choices: [
          { id: 'a', text: '1 cal/cm^2' },
          { id: 'b', text: '2 cal/cm^2' },
          { id: 'c', text: '1.2 cal/cm^2' },
          { id: 'd', text: '4 cal/cm^2' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q6',
        prompt:
          'Se pueden utilizar las tablas NFPA 70E 130.7 para calcular los factores de explosión de arco.',
        choices: [
          { id: 'a', text: 'Verdadero' },
          { id: 'b', text: 'Falso' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q7',
        prompt:
          'No se permite trabajar con energía a menos que sea _____________________.',
        choices: [
          { id: 'a', text: 'Exigido por su supervisor' },
          {
            id: 'b',
            text: 'Realizado por el director general y/o el equipo directivo',
          },
          {
            id: 'c',
            text: 'JUSTIFICADO por razones de riesgos adicionales, inviabilidad o menos de 50 voltios',
          },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q8',
        prompt:
          'Se puede utilizar un detector de voltaje de bolsillo para verificar adecuadamente la ausencia de voltaje.',
        choices: [
          { id: 'a', text: 'Verdadero' },
          { id: 'b', text: 'Falso' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q9',
        prompt:
          'Una persona calificada es aquella que _______________________ construcción y operación de equipos e instalaciones eléctricas, y ha recibido capacitación en seguridad para identificar los peligros y reducir el riesgo asociado.',
        choices: [
          {
            id: 'a',
            text: 'Ha tenido experiencia en la búsqueda de la ficha de datos de seguridad',
          },
          { id: 'b', text: 'Ha estudiado prácticas de trabajo seguras' },
          { id: 'c', text: 'Ha demostrado habilidades y conocimientos' },
          { id: 'd', text: 'Ha asistido a la capacitación NFPA 70E' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q10',
        prompt:
          'La cantidad de energía térmica, impresa en una superficie, a cierta distancia de la fuente, generada durante un arco eléctrico es',
        choices: [
          { id: 'a', text: 'Arco eléctrico' },
          { id: 'b', text: 'Energía incidente' },
          { id: 'c', text: 'Temperatura radiada' },
          { id: 'd', text: 'Calorías' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q11',
        prompt:
          'La OSHA exige el uso de guantes de goma con clasificación eléctrica (seleccionados según la exposición al voltaje) junto con sobreprotectores de cuero cuando:',
        choices: [
          {
            id: 'a',
            text: 'Confirmen la ausencia de voltaje en circuitos de más de 50 voltios.',
          },
          {
            id: 'b',
            text: 'Solucionen problemas en circuitos de más de 50 voltios.',
          },
          { id: 'c', text: 'Crucen el límite de aproximación restringida.' },
          { id: 'd', text: 'Todas las anteriores.' },
        ],
        correctChoiceId: 'd',
      },
      {
        id: 'q12',
        prompt:
          'Los guantes de hule con clasificación de voltaje deben someterse a pruebas ___________ e inspeccionarse __________.',
        choices: [
          { id: 'a', text: 'Anualmente, mensualmente.' },
          {
            id: 'b',
            text: 'Anualmente, solo si se sospecha que pueden fallar.',
          },
          { id: 'c', text: 'Cada seis meses, antes de cada uso.' },
          { id: 'd', text: 'Anualmente, semanalmente.' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q13',
        prompt:
          'La exposición a ______ se considera un riesgo de descarga eléctrica y electrocución.',
        choices: [
          { id: 'a', text: '50 voltios o más' },
          { id: 'b', text: '30 voltios o más' },
          { id: 'c', text: '24 voltios o más' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q14',
        prompt:
          'El límite del arco eléctrico se determina como la distancia desde el arco eléctrico en la que la energía incidente desciende a _________',
        choices: [
          { id: 'a', text: '200 grados F' },
          { id: 'b', text: '1.0 cal/cm^2' },
          { id: 'c', text: '1.2 cal/cm^2' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q15',
        prompt: 'Las barricadas se colocan (según lo exige la OSHA):',
        choices: [
          { id: 'a', text: 'Solo en el límite de acceso restringido' },
          { id: 'b', text: 'Solo en el límite de acceso limitado' },
          { id: 'c', text: 'Solo en el límite del arco eléctrico' },
          {
            id: 'd',
            text: 'En el límite de acceso limitado o en el límite del arco eléctrico, el que sea mayor',
          },
        ],
        correctChoiceId: 'd',
      },
      {
        id: 'q16',
        prompt:
          'Realizar una evaluación de riesgos "personal" antes de trabajar con un circuito eléctrico de más de 50 voltios es:',
        choices: [
          { id: 'a', text: 'Opcional' },
          {
            id: 'b',
            text: 'Comprender la probabilidad y la gravedad potencial de las lesiones asociadas con la realización de trabajos con energía eléctrica',
          },
          {
            id: 'c',
            text: 'Es obligatorio según la fórmula IEEE 1584-2018 para calcular la energía incidente',
          },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q17',
        prompt:
          '¿Cuál es el medio menos eficaz para reducir el riesgo asociado a un peligro?',
        choices: [
          { id: 'a', text: 'Controles técnicos' },
          { id: 'b', text: 'Controles administrativos' },
          { id: 'c', text: 'Sustitución' },
          { id: 'd', text: 'EPP' },
        ],
        correctChoiceId: 'd',
      },
      {
        id: 'q18',
        prompt: '¿Cuál es el propósito del bloqueo y etiquetado (LOTO)?',
        choices: [
          {
            id: 'a',
            text: 'Ayudar a los fabricantes de cerraduras a mantenerse en el negocio',
          },
          { id: 'b', text: 'Para frenarnos' },
          {
            id: 'c',
            text: 'Evitar que alguien venga detrás de nosotros y encienda lo que hemos apagado para trabajar de forma segura',
          },
          { id: 'd', text: 'Todas las anteriores' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q19',
        prompt:
          'La combinación de un equipo de uso diario de 8 cal/cm2 con un traje de protección contra arcos eléctricos de 40 cal/cm2 da una clasificación total del sistema de 48 cal/cm2?',
        choices: [
          { id: 'a', text: 'Verdadero' },
          { id: 'b', text: 'Falso' },
          { id: 'c', text: 'Depende de otros factores.' },
        ],
        // Answer key marks Falso (b); "Depende de otros factores" (c) is also
        // acceptable, so both count as correct.
        correctChoiceId: 'b',
        acceptedChoiceIds: ['b', 'c'],
      },
      {
        id: 'q20',
        prompt: 'La regla número uno de la seguridad eléctrica es:',
        choices: [
          {
            id: 'a',
            text: 'Utilizar el equipo de protección personal (EPP) independientemente de la situación en la que se encuentre.',
          },
          {
            id: 'b',
            text: 'Cualquier trabajo peligroso debe delegarse a la persona cualificada de mayor edad.',
          },
          {
            id: 'c',
            text: 'Considerar que todos los circuitos están energizados hasta que pueda confirmar por sí mismo que realmente están desenergizados.',
          },
          { id: 'd', text: 'Culpar a sus compañeros de trabajo.' },
        ],
        correctChoiceId: 'c',
      },
    ],
  },
  {
    // Qualified Electrical Worker REFRESHER (10 questions), transcribed from the
    // customer's "2027 QEW Refresher Exam" PDF (2026-10-05). Choices keep the
    // document's order, so the answer key supplied with it maps straight onto
    // the choice ids: 1C 2B 3A 4C 5B 6A 7B 8A 9D 10C. Two typos fixed ("must be
    // must be", "if its is"), TRUE/FALSE written as the other quizzes write it.
    //
    // Its own family, so taking the full exam does not block the refresher (or
    // the other way round), and its certificates say what it is.
    slug: 'electrical-safety-refresher',
    lang: 'en',
    shortTitle: 'QEW Refresher (EN)',
    family: 'qualified-worker-refresher',
    familyTitle: 'Qualified Workers Refresher (NFPA 70E)',
    // The course line on its certificates, in place of the full course's
    // "Electrical Safety & NFPA 70E <year> Standard Training". It names the
    // edition too since 2026-11-04 (Angel), from the default so the two move
    // together.
    certificateCourse: `Qualified Electrical Worker - NFPA 70E ${DEFAULT_NFPA_YEAR} Refresher Course`,
    // A 4-hour course, not the default 8 (Angel, 2026-10-06).
    certificateHours: 4,
    title:
      'Electrical Safety Exam for Qualified Electrical Workers - Refresher',
    description:
      'A refresher on the electrical-safety knowledge required of a qualified electrical worker.',
    // Same note as the full exam (Angel, 2026-10-05).
    note: 'You may use your book during the test.',
    passingScore: PASSING_SCORE,
    questions: [
      {
        id: 'q1',
        prompt:
          'What is the name of the Standard for Electrical Safety in the Workplace?',
        choices: [
          { id: 'a', text: 'OSHA 30' },
          { id: 'b', text: 'ASTM F-18' },
          { id: 'c', text: 'NFPA 70E' },
          { id: 'd', text: 'NFPA 101' },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q2',
        prompt: 'What are the 3 hazards of electricity?',
        choices: [
          { id: 'a', text: 'Burns, Cuts and Bruises' },
          { id: 'b', text: 'Shock, Arc Flash and Arc Blast' },
          { id: 'c', text: 'Slips, Trips and Falls' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q3',
        prompt:
          'A Safety Backup is REQUIRED anytime energized work is performed at or above 50 volts AC/DC.',
        choices: [
          { id: 'a', text: 'True' },
          { id: 'b', text: 'False' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q4',
        prompt:
          'Energized work is not allowed unless it is _____________________.',
        choices: [
          { id: 'a', text: 'Demanded by your supervisor' },
          { id: 'b', text: 'Performed by the CEO and/or management team' },
          {
            id: 'c',
            text: 'JUSTIFIED for reasons of Additional Hazards, Infeasibility or Less than 50 Volts.',
          },
        ],
        correctChoiceId: 'c',
      },
      {
        id: 'q5',
        prompt: 'What is the purpose of Lockout Tagout (LOTO)?',
        choices: [
          { id: 'a', text: 'To help the lock manufacturers stay in business' },
          {
            id: 'b',
            text: 'To keep someone from coming behind us and turning on what we turned off to work on safely',
          },
          { id: 'c', text: 'To slow us down' },
          { id: 'd', text: 'All of the above' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q6',
        prompt:
          'OSHA defines hazardous voltage as _______ and above AC and DC.',
        choices: [
          { id: 'a', text: '50 volts' },
          { id: 'b', text: '110 volts' },
          { id: 'c', text: '480 volts' },
          { id: 'd', text: '4,160 volts' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q7',
        prompt:
          'There are how many steps to confirm a circuit is de-energized?',
        choices: [
          { id: 'a', text: '3' },
          { id: 'b', text: '8' },
          { id: 'c', text: '6' },
          { id: 'd', text: '4' },
        ],
        correctChoiceId: 'b',
      },
      {
        id: 'q8',
        prompt:
          'At what Incident Energy level is the potential onset of second degree burns?',
        choices: [
          { id: 'a', text: '1.2 cal/cm²' },
          { id: 'b', text: '2.5 cal/cm²' },
          { id: 'c', text: '40 cal/cm²' },
          { id: 'd', text: '140 cal/cm²' },
        ],
        correctChoiceId: 'a',
      },
      {
        id: 'q9',
        prompt:
          'Voltage rated rubber gloves must be tested ____________ and inspected ______________.',
        choices: [
          { id: 'a', text: 'Annually and inspected monthly' },
          {
            id: 'b',
            text: 'Annually and inspected only if failure is suspected',
          },
          { id: 'c', text: 'Annually and inspected weekly' },
          { id: 'd', text: 'Every 6 months and inspected before each use' },
        ],
        correctChoiceId: 'd',
      },
      {
        id: 'q10',
        prompt: 'The number one rule of electrical safety is:',
        choices: [
          {
            id: 'a',
            text: 'Wear your PPE only if it is comfortable and easily accessed',
          },
          {
            id: 'b',
            text: 'Any dangerous job should be passed on to the oldest qualified worker',
          },
          {
            id: 'c',
            text: 'Consider all circuits energized until you can confirm for yourself (through the 8 step method) that it is actually de-energized',
          },
          { id: 'd', text: 'Blame your co-workers' },
        ],
        correctChoiceId: 'c',
      },
    ],
  },
]

// Slugs of every quiz. Handy for route validation and building the QR/link set.
export const quizSlugs = QUIZZES.map((q) => q.slug)

// Look up a quiz by its slug. Returns null when the slug is unknown so callers
// (the route guard / view) can redirect to 404.
export function getQuiz(slug) {
  return QUIZZES.find((q) => q.slug === slug) || null
}

// Grade a completed attempt. `answers` is a map of { [questionId]: choiceId }.
// Returns the raw counts plus an integer percentage `score` (0–100) and a
// `passed` boolean. Passing compares the exact ratio to the threshold (not the
// rounded score) so e.g. 7/10 = 0.70 passes a 70% quiz cleanly.
// A choice is correct if it matches the question's correctChoiceId, or, when a
// question accepts more than one answer, if it's in acceptedChoiceIds.
function isCorrectChoice(question, choiceId) {
  if (
    Array.isArray(question.acceptedChoiceIds) &&
    question.acceptedChoiceIds.length
  ) {
    return question.acceptedChoiceIds.includes(choiceId)
  }
  return choiceId === question.correctChoiceId
}

export function gradeQuiz(quiz, answers = {}) {
  const total = quiz.questions.length
  const correctCount = quiz.questions.reduce(
    (n, q) => (isCorrectChoice(q, answers[q.id]) ? n + 1 : n),
    0
  )
  const ratio = total === 0 ? 0 : correctCount / total
  const score = Math.round(ratio * 100)
  const threshold = quiz.passingScore ?? PASSING_SCORE
  const passed = total > 0 && ratio >= threshold
  return { total, correctCount, score, passed }
}

// Per-question breakdown for the post-submission review: every choice, flagged
// with whether the taker picked it and whether it counts as correct. Shares
// isCorrectChoice with gradeQuiz, so a reviewed question can never disagree with
// the score. Including questions that accept more than one answer, where more
// than one choice comes back `correct: true`.
export function reviewQuiz(quiz, answers = {}) {
  if (!quiz) return []
  return quiz.questions.map((question, index) => {
    const selectedId = answers[question.id]
    return {
      id: question.id,
      number: index + 1,
      prompt: question.prompt,
      answered: !!selectedId,
      correct: isCorrectChoice(question, selectedId),
      choices: question.choices.map((choice) => ({
        id: choice.id,
        text: choice.text,
        selected: choice.id === selectedId,
        correct: isCorrectChoice(question, choice.id),
      })),
    }
  })
}
